/**
 * 学習者モデル。1 文字ごとの習得段階と、その遷移・減衰・出題重みを決める純関数群。
 *
 * ここには DOM も Date.now も Math.random も持ち込まない。時刻は「ローカル暦日の通し番号」
 * として外から渡され、判定はすべて引数だけで決まる。保存形式（progress-store.ts）と
 * 出題側（quiz-controller.ts）の両方がこのファイルを見るので、数値の根拠はここに書く。
 */

/**
 * 1 文字ぶんの記録。配列にしているのは localStorage に入れる JSON を小さく保つため
 * （510 字を最大値で埋めても 20KB に届かない）。
 *
 *   [箱, 最終回答日, 回答数, 誤答数, 学習した日数]
 *
 * 箱は 1〜5 の整数。レコードが無い＝未学習で、箱 0 は作らない。
 * ミリ秒は文字ごとに保存しない。反応時間はその場の判定にだけ使い、減衰は日単位で行う。
 */
export type Rec = [box: number, day: number, answered: number, wrong: number, days: number];

/** 回答の分類。不正解は反応時間を見ない */
export type Outcome = 'fast' | 'slow' | 'untimed' | 'wrong';

/**
 * これより遅い正解は「計測不能」として箱を動かさない。
 * 6 秒にすると ṣṭha 級のラベルを読み比べる初心者の正解が軒並み捨てられる。
 */
export const UNTIMED_MS = 20_000;

/** 個人適応を始める正解 RT の件数。それまでは基準式だけを使う */
export const FAST_ADAPT_MIN = 20;
/** 個人適応の上限。ここまでしか緩めない */
export const FAST_MAX_MS = 5_000;
/** 個人適応に使う直近の正解 RT の件数（保存側の上限と一致させる） */
export const RT_WINDOW = 32;

/** 箱が 1 つ下がるごとに出題重みが 1.6 倍になる。定数はこの 1 個だけ */
const GROWTH = 1.6;

/**
 * 箱ごとの保持見込み（日）。この日数が経つごとに実効箱が 1 つ下がる。
 * 添字 = 箱。箱 1・2 は減衰しない（0 は使わない）。
 */
export const RETENTION_DAYS = [0, 0, 3, 7, 16, 35] as const;

/**
 * 「速い正解」の基準（ms）。
 *
 * 字形の再認 250〜400ms ＋ IAST ラベル 1 個の読み取り 350〜500ms × 選択肢数
 * ＋ タップ 250ms ＋ 判断の余裕 250ms。スライダーが 2 のときは選択肢が 2 個になるので、
 * 選択肢数に比例させないと 2 択と 3 択で閾値の意味が変わる。
 */
export function baseFastMs(optionCount: number): number {
  return 1500 + 600 * optionCount;
}

/**
 * 「速い正解」の閾値。基準式を下限に、**上方向にだけ**個人適応する。
 *
 * 固定閾値だと読むのが遅い成人が全員箱 2 で頭打ちになり、定着が一生付かない。
 * 一方で自由に動く閾値は分布が読めなくなる。直近の正解 RT の中央値 × 1.5 を
 * 基準式と 5 秒の間にクランプすることで、厳しくなる方向には決して動かない。
 * 閾値は UI に出さない（速さの最適化を誘発する）。
 */
export function fastThreshold(rts: readonly number[], optionCount: number): number {
  const base = baseFastMs(optionCount);
  if (rts.length < FAST_ADAPT_MIN) return base;
  const sorted = rts.slice(-RT_WINDOW).sort((a, b) => a - b);
  const mid = sorted.length / 2;
  const median =
    sorted.length % 2 === 1 ? sorted[Math.floor(mid)] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.min(FAST_MAX_MS, Math.max(base, 1.5 * median));
}

export function classify(
  correct: boolean,
  elapsedMs: number | undefined,
  fastMs: number
): Outcome {
  if (!correct) return 'wrong';
  if (elapsedMs === undefined || !Number.isFinite(elapsedMs) || elapsedMs > UNTIMED_MS) {
    return 'untimed';
  }
  return elapsedMs <= fastMs ? 'fast' : 'slow';
}

/**
 * 遷移表。添字は現在の箱（0 = 未学習）。
 *
 * - 遅い正解は昇格させない（据え置き。罰にはならない）。箱 5 の遅い正解だけは
 *   「定着＝自動的に読める」の反証なので 4 に落とす。
 * - 計測不能な正解は箱を動かさない。ただし未学習からは 2 にする。箱 2 の重みは
 *   未学習と同じなので出題は変わらず、「正解した字を苦手と表示する」嘘だけを避けられる。
 * - 不正解は箱 4/5 からは 2 まで、1〜3 からは 1 まで。1 回の取りこぼしで積み上げを
 *   全部捨てない。
 * - 箱 4 → 5 は学習した日が 2 日以上のときだけ（nextRec）。初日は何度速答しても 4 で
 *   止まる。箱 5 は「別の日にも読めた」の証拠なので、同じ日の反復では埋めない
 */
const TRANSITIONS: Record<Outcome, readonly number[]> = {
  fast: [2, 2, 3, 4, 5, 5],
  slow: [1, 1, 2, 3, 4, 4],
  untimed: [2, 1, 2, 3, 4, 5],
  wrong: [1, 1, 1, 1, 2, 2],
};

/**
 * 箱 5 は 2 暦日目から。同じ日に何度速答しても 4 で止まり、別の日の速い正解で初めて 5 になる。
 * これで「実効箱 5」がそのまま「別の暦日に 2 回以上」を含意し、初日だけの字が箱 5 の
 * 保持見込み（35 日）で減衰を待つことがない（箱 4 の 16 日で減衰する）
 */
export function nextRec(rec: Rec | undefined, outcome: Outcome, today: number): Rec {
  const box = rec ? rec[0] : 0;
  let next = TRANSITIONS[outcome][box];
  const wrong = outcome === 'wrong' ? 1 : 0;
  if (!rec) return [next, today, 1, wrong, 1];
  const days = rec[4] + (rec[1] !== today ? 1 : 0);
  if (next === 5 && box < 5 && days < 2) next = box;
  return [next, today, rec[2] + 1, rec[3] + wrong, days];
}

/**
 * 減衰を適用した実効箱。保存値は書き換えないので、何度読んでも二重に下がらない。
 *
 * 箱 5 は 35 日で 4、70 日で 3、105 日以降は 2 で止まる。ゼロには戻らないので
 * 「久しぶりに開いたら全部リセット」にならない。下限を 2 にしているのは、
 * 箱 1（＝苦手）を「観測された誤り」に予約するため。放置しただけの字で
 * 「まだ曖昧な字」の一覧を埋めない。
 */
export function effBox(rec: Rec, today: number): number {
  const box = rec[0];
  if (box <= 2) return box;
  const elapsed = Math.max(0, today - rec[1]); // 端末時計が戻っていたら経過 0 とみなす
  const steps = Math.min(3, Math.floor(elapsed / RETENTION_DAYS[box]));
  return Math.max(2, box - steps);
}

/**
 * 出題重み。苦手 6.55 : あいまい・未学習 4.10 : 学習中 2.56 : ほぼ定着 1.60 : 定着 1.00。
 * 履歴が空なら全員 4.10 で等しい（＝一様）ことが式のレベルで自明になっている。
 */
export function weightFor(rec: Rec | undefined, today: number): number {
  const box = rec ? effBox(rec, today) : 2;
  return GROWTH ** (5 - box);
}

export type Label = '未学習' | '苦手' | 'あいまい' | '学習中' | 'ほぼ定着' | '定着';

/**
 * 画面に出す状態。依頼文の「まだ曖昧と考えられる文字」は実効箱 ≤ 2（苦手・あいまい）。
 *
 * 「定着」は実効箱 5 かつ**別の暦日に 2 回以上**回答していること。箱 5 は 2 暦日目からしか
 * 付かない（nextRec）ので、新しい記録では実効箱 5 = 定着。学習日 1 日のまま箱 5 を持つのは
 * 上限を入れる前の版が書いた記録で、「ほぼ定着」として扱う（別の日の正解で定着になる）。
 * 一度も間違えていない字（実効箱 1 かつ誤答 0。未学習からの遅い正解でだけ起こる）を
 * 「苦手」とは表示しない。
 */
export function labelFor(rec: Rec | undefined, today: number): Label {
  if (!rec) return '未学習';
  const box = effBox(rec, today);
  if (box === 1) return rec[3] >= 1 ? '苦手' : 'あいまい';
  if (box === 2) return 'あいまい';
  if (box === 3) return '学習中';
  if (box === 4) return 'ほぼ定着';
  return rec[4] >= 2 ? '定着' : 'ほぼ定着';
}

/**
 * ローカル暦日の通し番号。現地の年月日から直接作るので、時差にも夏時間にも依存せず
 * 必ず 1 日ごとに +1 する。epoch を丸める方式は時差が +12 を超える地域で値が 1 ずれる
 * （日本では同じ値になる）。
 */
export function localDay(now: number = Date.now()): number {
  const d = new Date(now);
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000);
}
